import { cva, type VariantProps } from 'class-variance-authority';
import { Avatar as RadixAvatar } from 'radix-ui';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';

import { cn } from '../../lib/cn';

const avatar = cva(
  [
    'inline-flex shrink-0 select-none items-center justify-center overflow-hidden',
    // A square tile, not a disc. Nothing in this product is round.
    'rounded-control border border-border bg-surface-sunk',
    'font-sans font-bold uppercase text-ink-subtle',
  ],
  {
    variants: {
      size: {
        sm: 'size-8 text-chip',
        /** The account block, and any list row. */
        md: 'size-11 text-body-compact',
        lg: 'size-touch text-subject',
      },
      surface: {
        default: '',
        /** Inside the slate sidebar's account block. */
        chassis: 'border-transparent bg-chassis-raised text-ink-inverse',
      },
    },
    defaultVariants: { size: 'md', surface: 'default' },
  },
);

export type AvatarVariants = VariantProps<typeof avatar>;

export interface AvatarProps
  extends Omit<ComponentPropsWithoutRef<typeof RadixAvatar.Root>, 'children'>,
    AvatarVariants {
  /**
   * The person's full name. Used for the accessible name and, when no `src` is
   * given, to derive the initials.
   */
  readonly name: string;
  readonly src?: string;
  /**
   * Pre-computed initials. Derive them yourself when the locale's rules differ
   * from "first letter of the first two words" — "Pierre-Luc Thériault" is PT.
   */
  readonly initials?: string;
  /**
   * The tile sits BESIDE the name it stands for, so it says nothing new.
   *
   * The default carries an `sr-only` name, which is right on a roster where a
   * bare tile would otherwise be an unlabelled box — and wrong on an identity
   * card, where a screen reader then reads "Marie Tremblay" twice in a row.
   * `decorative` removes the tile from the accessibility tree entirely rather
   * than making every such caller remember `aria-hidden`.
   */
  readonly decorative?: boolean;
}

function deriveInitials(name: string): string {
  const parts = name
    .split(/[\s-]+/u)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  const first = parts[0]?.[0] ?? '';
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return `${first}${second}`.toUpperCase();
}

/**
 * A 44 px square tile carrying initials, or a photo when one exists.
 *
 * DESIGN.md originally sanctioned a 28 px disc as the one circle in the system.
 * Two things killed it: 28 px is below every pointer floor the product commits
 * to, and a lone circle in a world of 4 px corners reads as an import from
 * another product. The tile is the same idea at a size a thumb can hit.
 */
export const Avatar = forwardRef<ElementRef<typeof RadixAvatar.Root>, AvatarProps>(function Avatar(
  { name, src, initials, size, surface, decorative = false, className, ...rest },
  ref,
) {
  return (
    <RadixAvatar.Root
      {...rest}
      ref={ref}
      {...(decorative ? { 'aria-hidden': true } : {})}
      className={cn(avatar({ size, surface }), className)}
    >
      {src !== undefined ? (
        <RadixAvatar.Image
          src={src}
          alt={decorative ? '' : name}
          className="size-full object-cover"
        />
      ) : null}
      <RadixAvatar.Fallback
        // No delay when there is no image: a fallback that waits for a timer
        // renders an empty tile on first paint, and on a roster of 40 people
        // that is 40 empty tiles.
        {...(src !== undefined ? { delayMs: 300 } : {})}
        className="flex size-full items-center justify-center"
      >
        <span aria-hidden="true">{initials ?? deriveInitials(name)}</span>
        {decorative ? null : <span className="sr-only">{name}</span>}
      </RadixAvatar.Fallback>
    </RadixAvatar.Root>
  );
});
